import {
  ActionIdentifier,
  InspirationAnimationPrompt,
  InspirationGuide,
  InspirationImagePrompt,
} from "@/lib/stores/editor-store/creative-project-state";

const ANIMATE_VIDEO_STEP = {
  title: "Animate video",
  description: "Select a prompt, modify it if you want and click **Animate**.",
  actionIdentifier: ActionIdentifier.ANIMATE_VIDEO,
};

const GENERATE_IMAGE_STEP = {
  title: "Generate image",
  description:
    "Select a prompt, modify it if you want and click **Generate image**.",
  actionIdentifier: ActionIdentifier.GENERATE_IMAGE,
};

const NAVIGATE_TO_START_IMAGE_EDIT_STEP = {
  title: "Go to image edit",
  description: "Click **Edit image** to edit the start image.",
  actionIdentifier: ActionIdentifier.NAVIGATE_START_IMAGE,
};

const NAVIGATE_TO_END_IMAGE_EDIT_STEP = {
  title: "Go to image edit",
  description: "Click **Edit image** to edit the end image.",
  actionIdentifier: ActionIdentifier.NAVIGATE_END_IMAGE,
};

const NAVIGATE_TO_CUSTOM_ANIMATION_STEP = {
  title: "Go to custom animation",
  description:
    "Once you are happy with the image, click **Use for animation**.",
  actionIdentifier: ActionIdentifier.NAVIGATE_ANIMATION,
};

const ADD_END_IMAGE_STEP = {
  title: "Add end image",
  description: "Click **End** and add the image you want to use.",
  actionIdentifier: ActionIdentifier.ADD_END_IMAGE,
};

const SELECT_ACTORS_STEP = {
  title: "Select actors",
  description:
    "Click **Add elements** and choose the actors you want to use, 1-4 actors are recommended.",
  actionIdentifier: ActionIdentifier.SELECT_ELEMENTS,
};

const SINGLE_ACTOR_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "SINGLE_ACTOR",
  name: "Single Actor",
  customInput: {
    prompt:
      "Place the person into the first image in a natural, candid position as if the person is genuinely using the space.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading, and fixed structures of the original scene in the first image. Insert only the person in the reference image. The inserted person should be adapted to the scene: match the person's lighting, exposure, and scale so the person appears realistically sized and blends in seamlessly.",
  },
};

const SINGLE_ACTOR_ANIMATION_PROMPT: InspirationAnimationPrompt = {
  id: "SINGLE_ACTOR",
  name: "Single Actor",
  customInput: {
    prompt:
      "Photorealistic cinematic scene of a person using the space in a natural, candid way. The camera remains almost still, with a subtle forward movement.",
    duration: 5,
  },
  hasPreviewVideo: true,
};

const MULTIPLE_ACTORS_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "MULTIPLE_ACTORS",
  name: "Multiple Actors",
  customInput: {
    prompt:
      "Place the persons into the first image in natural, candid positions, spending time together.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading, and fixed structures of the original scene in the first image. Insert only the persons in the reference images. The inserted persons should be adapted to the scene: match their lighting, exposure, and scale so they appear realistically sized and blend in seamlessly.",
  },
};

const MULTIPLE_ACTORS_ANIMATION_PROMPT: InspirationAnimationPrompt = {
  id: "MULTIPLE_ACTORS",
  name: "Multiple Actors",
  customInput: {
    prompt:
      "Photorealistic cinematic scene of people casually spending time together. The camera remains almost still, with a subtle forward movement.",
    duration: 5,
  },
  hasPreviewVideo: true,
};

const CLOSE_UP_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "CLOSE_UP",
  name: "Close-Up",
  customInput: {
    prompt:
      "An extreme close-up of one of the people in the image.\n\nThe close-up should be from the exact same moment, like the very next shot in a movie. Only one person should be visible in the close-up. Keep everyone's exact positions, poses, expressions, clothing, and the lighting identical to the original image — only the camera moves closer.",
  },
};

const CLOSE_UP_ANIMATION_PROMPT: InspirationAnimationPrompt = {
  id: "CLOSE_UP",
  name: "Close-Up",
  customInput: {
    prompt:
      "Photorealistic cinematic close-up of a person talking naturally. The camera remains still mounted on a tripod.",
    duration: 5,
  },
  hasPreviewVideo: true,
};

const WALK_IN_ACTOR_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "WALK_IN_ACTOR",
  name: "Walk-In Actor",
  customInput: {
    prompt:
      "Place the person into the first image in a natural, candid position as if the person is genuinely using the space.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading, and fixed structures of the original scene in the first image. Insert only the person in the reference image. The inserted person should be adapted to the scene: match the person's lighting, exposure, and scale so the person appears realistically sized and blends in seamlessly.",
  },
};

const WALK_IN_ACTOR_ANIMATION_PROMPT: InspirationAnimationPrompt = {
  id: "WALK_IN_ACTOR",
  name: "Walk-In Actor",
  customInput: {
    prompt:
      "Photorealistic cinematic scene of a person walking into the space in a natural, candid way. The camera remains still mounted on a tripod.",
    duration: 5,
  },
  hasPreviewVideo: true,
};

const SPEED_RAMP_ANIMATION_PROMPT: InspirationAnimationPrompt = {
  id: "SPEED_RAMP",
  name: "Speed Ramp",
  customInput: {
    prompt:
      "Smooth cinematic speed ramp with continuous spatial movement. The camera moves through the scene, beginning from a full stop, accelerating through the middle, then easing out to a full stop at the end.",
    duration: 3,
  },
  hasPreviewVideo: true,
};

const SCANDINAVIAN_STAGING_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "SCANDINAVIAN",
  name: "Scandinavian",
  customInput: {
    prompt:
      "Don't change the physical structures. Add Scandinavian-style furniture and decor that fit the room's purpose.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading of the scene in the original image.",
  },
};

const MODERN_STAGING_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "MODERN",
  name: "Modern",
  customInput: {
    prompt:
      "Don't change the physical structures. Add modern-style furniture and decor with clean lines, neutral tones, and uncluttered surfaces that fit the room's purpose.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading of the scene in the original image.",
  },
};

const MID_CENTURYSTAGING_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "MID_CENTURY",
  name: "Mid-Century",
  customInput: {
    prompt:
      "Don't change the physical structures. Add mid-century modern furniture and decor with warm wood tones, tapered legs, and iconic 1950s-60s silhouettes that fit the room's purpose.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading of the scene in the original image.",
  },
};

const MODERN_FARMHOUSE_STAGING_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "FARMHOUSE",
  name: "Farmhouse",
  customInput: {
    prompt:
      "Don't change the physical structures. Add farmhouse-style furniture and decor with shiplap accents, weathered wood, neutral linens, and matte black hardware that fit the room's purpose.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading of the scene in the original image.",
  },
};

const COASTAL_STAGING_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "COASTAL",
  name: "Coastal",
  customInput: {
    prompt:
      "Don't change the physical structures. Add coastal-style furniture and decor with light woods, soft blues and whites, natural fibers, and an airy beach-inspired feel that fit the room's purpose. Avoid nautical clichés like ropes, anchors, or seashells.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading of the scene in the original image.",
  },
};

const INDUSTRIAL_STAGING_IMAGE_PROMPT: InspirationImagePrompt = {
  id: "INDUSTRIAL",
  name: "Industrial",
  customInput: {
    prompt:
      "Don't change the physical structures. Add industrial-style furniture and decor with raw materials, exposed metal, dark woods, and leather accents that fit the room's purpose.\n\nPreserve the exact framing, exposure, brightness, contrast, color grading of the scene in the original image.",
  },
};

const AERIAL_TRANSITION_ANIMATION_PROMPT: InspirationAnimationPrompt = {
  id: "AERIAL_TRANSITION",
  name: "Aerial Transition",
  customInput: {
    prompt:
      "Smooth cinematic transition with continuous spatial movement. The camera moves through the scene at a constant speed from start to finish, with steady, stabilised motion throughout.",
    duration: 5,
  },
  hasPreviewVideo: true,
};

export const INSPIRATION_GUIDES: InspirationGuide[] = [
  {
    id: "ACTORS",
    title: "Actors",
    description:
      "Actors draw focus and give your scene a clear subject for viewers to connect with.",
    steps: [
      NAVIGATE_TO_START_IMAGE_EDIT_STEP,
      SELECT_ACTORS_STEP,
      GENERATE_IMAGE_STEP,
      NAVIGATE_TO_CUSTOM_ANIMATION_STEP,
      ANIMATE_VIDEO_STEP,
    ],
    imagePrompts: [SINGLE_ACTOR_IMAGE_PROMPT, MULTIPLE_ACTORS_IMAGE_PROMPT],
    animationPrompts: [
      SINGLE_ACTOR_ANIMATION_PROMPT,
      MULTIPLE_ACTORS_ANIMATION_PROMPT,
    ],
    hasPreviewVideo: true,
  },

  {
    id: "SPEED_RAMP",
    title: "Speed Ramp",
    description:
      "Speed Ramp lets you create a transition between two images that starts from a standstill, accelerates through the middle, and eases out at the end.",
    steps: [ADD_END_IMAGE_STEP, ANIMATE_VIDEO_STEP],
    animationPrompts: [SPEED_RAMP_ANIMATION_PROMPT],
    hasPreviewVideo: true,
  },
  {
    id: "VIRTUAL_STAGING",
    title: "Virtual Staging",
    description:
      "Virtual staging brings empty rooms to life with furniture and decor, helping viewers see the space's potential.",
    steps: [],
    imagePrompts: [
      SCANDINAVIAN_STAGING_IMAGE_PROMPT,
      MODERN_STAGING_IMAGE_PROMPT,
      MID_CENTURYSTAGING_IMAGE_PROMPT,
      MODERN_FARMHOUSE_STAGING_IMAGE_PROMPT,
      COASTAL_STAGING_IMAGE_PROMPT,
      INDUSTRIAL_STAGING_IMAGE_PROMPT,
    ],
  },
  {
    id: "AERIAL_TRANSITION",
    title: "Aerial Transition",
    description:
      "Aerial Transition lets you create a transition between two images that moves smoothly and uniformly, maintaining a constant speed from start to finish.",
    steps: [ADD_END_IMAGE_STEP, ANIMATE_VIDEO_STEP],
    animationPrompts: [AERIAL_TRANSITION_ANIMATION_PROMPT],
    hasPreviewVideo: true,
  },

  {
    id: "CLOSE_UP",
    title: "Close-Up",
    description:
      "A close-up on a single person makes the moment feel intimate. Start from an image that already includes people, such as one created with the **Multiple Actors** guide.",
    steps: [
      NAVIGATE_TO_START_IMAGE_EDIT_STEP,
      GENERATE_IMAGE_STEP,
      NAVIGATE_TO_CUSTOM_ANIMATION_STEP,
      ANIMATE_VIDEO_STEP,
    ],
    imagePrompts: [CLOSE_UP_IMAGE_PROMPT],
    animationPrompts: [CLOSE_UP_ANIMATION_PROMPT],
    hasPreviewVideo: true,
  },
  {
    id: "WALK_IN_ACTOR",
    title: "Walk-In Actor",
    description:
      "A walk-in actor enters from off-camera, bringing movement and life to the scene.",
    steps: [
      ADD_END_IMAGE_STEP,
      NAVIGATE_TO_END_IMAGE_EDIT_STEP,
      SELECT_ACTORS_STEP,
      GENERATE_IMAGE_STEP,
      NAVIGATE_TO_CUSTOM_ANIMATION_STEP,
      ANIMATE_VIDEO_STEP,
    ],
    imagePrompts: [WALK_IN_ACTOR_IMAGE_PROMPT],
    animationPrompts: [WALK_IN_ACTOR_ANIMATION_PROMPT],
    hasPreviewVideo: true,
  },
];
